import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IParamsType } from '@/utils/ctype';
import { getExamInfo, IGetExamInfo } from '@/api/start';

export interface IExam {
  info: IGetExamInfo | null;
  requestId?: string;
  loadStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  submittedWritingRecordIds: number[];
}

const initialState: IExam = {
  info: null,
  loadStatus: 'idle',
  submittedWritingRecordIds: [],
};

export const getExamInfoAsync = createAsyncThunk<IGetExamInfo, IParamsType>('exam/getExamInfoAsync', async (params, {rejectWithValue, signal}) => {
  try {
    const res = await getExamInfo(params, {signal});
    if (res.code !== 200 || res.data?.record_id !== Number(params.record_id)) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }
});

export const examSlice = createSlice({
  name: 'exam',
  initialState,
  reducers: {
    writingSubmissionConfirmed(state, {payload: recordId}: PayloadAction<number>) {
      if (!state.submittedWritingRecordIds.includes(recordId)) {
        state.submittedWritingRecordIds.push(recordId);
      }
      if (state.info?.record_id === recordId && state.info.writing.status < 4) {
        state.info.writing.status = 3;
        state.info.writing.submission_pending = false;
        state.info.writing.is_confirm = 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getExamInfoAsync.pending, (state, action) => {
      state.requestId = action.meta.requestId;
      state.loadStatus = 'loading';
      if (state.info?.record_id !== Number(action.meta.arg.record_id)) {
        state.info = null;
      }
    });
    builder.addCase(getExamInfoAsync.fulfilled, (state, {payload, meta}) => {
      if (state.requestId !== meta.requestId) return;
      state.loadStatus = 'succeeded';
      const writing = payload.writing;
      if ((writing.status === 4 || (writing.status === 3 && writing.submission_pending === false))
        && !state.submittedWritingRecordIds.includes(payload.record_id)) {
        state.submittedWritingRecordIds.push(payload.record_id);
      }
      // A committed submission cannot become editable because a replica or
      // an earlier request still returns the pre-submission state.
      const confirmed = state.submittedWritingRecordIds.includes(payload.record_id);
      const scoredWriting = state.info?.record_id === payload.record_id && state.info.writing.status === 4
        ? state.info.writing : undefined;
      state.info = {
        ...payload,
        writing: scoredWriting && writing.status < 4 ? scoredWriting
          : confirmed && writing.status < 4 ? {...writing, status: 3, submission_pending: false, is_confirm: 1}
            : writing,
      };
    });
    builder.addCase(getExamInfoAsync.rejected, (state, action) => {
      if (state.requestId === action.meta.requestId) state.loadStatus = 'failed';
    });
  },
});
